import {Artist} from '@app/web-player/artists/artist';
import {PaginationResponse} from '@common/http/backend-response/pagination-response';
import {Album} from '@app/web-player/albums/album';
import {AlbumGridItem} from '@app/web-player/albums/album-grid-item';
import {
  ArtistAlbumQueryOptions,
  useArtistAlbums,
  useArtistAlbumsSubtype,
} from '@app/web-player/artists/requests/use-artist-albums';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';
import {NoDiscographyMessage} from '@app/web-player/artists/artist-page/discography-panel/no-discography-message';
import {ContentGrid} from '@app/web-player/playable-item/content-grid';
import { AlbumSubtype } from '@common/admin/use-album_taxonomies';
import { Trans } from '@ui/i18n/trans';
import { IconButton } from '@ui/buttons/icon-button';
import { GridViewIcon } from '@ui/icons/material/GridView';
import { ViewAgendaIcon } from '@ui/icons/material/ViewAgenda';
import {Tooltip} from '@ui/tooltip/tooltip';
import { getPageLabel } from '../../artist-label';
import { useCookie } from '@ui/utils/hooks/use-cookie';
import { albumLayoutKey } from '@app/web-player/artists/requests/use-artist';
import { useSettings } from '@ui/settings/use-settings';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { useEffect, useRef } from 'react';
import {getAlbumTypeContentFormat} from '@app/web-player/library/album-type-utils';

interface ArtistAlbumsGridProps {
  artist: Artist;
  initialAlbums: PaginationResponse<Album> | null;
  albumQueryOptions?: ArtistAlbumQueryOptions;
  showEmptyMessage?: boolean;
}
export function ArtistAlbumsGrid({
  artist,
  initialAlbums,
  albumQueryOptions,
  showEmptyMessage = true,
}: ArtistAlbumsGridProps) {
  const query = useArtistAlbums(
    initialAlbums,
    'grid',
    artist ? artist.id : undefined,
    albumQueryOptions,
  );

  if (!query.isLoading && !query.items.length) {
    if (!showEmptyMessage) {
      return null;
    }
    return <NoDiscographyMessage />;
  }

  return (
    <ContentGrid>
      {query.items.map(album => (
        <AlbumGridItem key={album.id} album={album} />
      ))}
      <InfiniteScrollSentinel query={query} />
    </ContentGrid>
  );
}

interface ArtistAlbumsSubtypeGridProps {
  artist: Artist;
  subtype: AlbumSubtype;
  selectedAlbumLayout?: string;
  initialAlbums: PaginationResponse<Album> | null;
  onLoaded?: () => void;
  albumQueryOptions?: ArtistAlbumQueryOptions;
}
export function ArtistAlbumsSubtypeGrid({
  artist,
  subtype,
  selectedAlbumLayout,
  initialAlbums,
  onLoaded,
  albumQueryOptions,
}: ArtistAlbumsSubtypeGridProps) {
  const {player} = useSettings();
  const query = useArtistAlbumsSubtype(
    initialAlbums,
    'grid',
    subtype.slug,
    artist ? artist.id : undefined,
    albumQueryOptions,
  );
  const {isLoading, items} = query;
  const [viewMode, setViewMode] = useCookie(
    albumLayoutKey,
    selectedAlbumLayout || player?.default_artist_view || 'grid',
  );
  const calledRef = useRef(false);
  
  useEffect(() => {
    if (!isLoading && onLoaded && !calledRef.current) {
      onLoaded();
      calledRef.current = true;
    }
  }, [isLoading, onLoaded]);

  if (isLoading || (!isLoading && !items.length)) {
    return null;
  }
  
  return (
    <>
      <div className="mt-30 mb-30 flex items-center border-b pb-4 text-muted">
        <h2 className="mr-auto text-base">
          <Trans message={subtype.name} />
        </h2>
        {/* <Tooltip label={<Trans message="List view" />}>
          <IconButton
            className="ml-24 flex-shrink-0"
            color={viewMode === 'list' ? 'primary' : undefined}
            onClick={() => setViewMode('list')}
          >
            <ViewAgendaIcon />
          </IconButton>
        </Tooltip>
        <Tooltip label={<Trans message="Grid view" />}>
          <IconButton
            className="flex-shrink-0"
            color={viewMode === 'grid' ? 'primary' : undefined}
            onClick={() => setViewMode('grid')}
          >
            <GridViewIcon />
          </IconButton>
        </Tooltip> */}
      </div>
      <ContentGrid>
        {items.map(album => (
          <AlbumGridItem
            key={album.id}
            album={album}
            contentFormat={getAlbumTypeContentFormat(album.type.slug) ?? 'square'}
          />
        ))}
        <InfiniteScrollSentinel query={query} />
      </ContentGrid>
    </>
  );
}
